import { ref, computed } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ErrorCodes, type BaseError, MetaErrorCodes, type Meta } from '@cplug/frontend-core/models'
import { BaseCRUDStore } from '@cplug/frontend-core/stores'
import { type App, AppKind, isAppSyncable, PushNotificationStatus, SmartMenuFunctionality, type TotemApp } from '../models/App'
import type { AppDuplicatePayload } from '../models/AppDuplicatePayload'
import { CPAppGateway } from '../gateways/CPAppGateway'
import type { AppGroup } from '../models/AppGroup'
import { CPAppGroupGateway } from '../gateways/CPAppGroupGateway'
import { DesktopModuleKind, type DesktopModuleKDS, TotemSubtype, type VpnSetting } from '../models/AppSetting'
import type { AppCredentials } from '../models/AppCredentials'
import type { AppLinkQrToken } from '../models/AppLinkQrToken'
import type { AppRevokeLicensePayload } from '../models/AppRevokeLicensePayload'
import { useAppPushNotificationStatus } from '../composables/useAppPushNotificationStatus'
import {
  STORE_DESCRIPTION_MAX_LENGTH,
  STORE_DISPLAY_NAME_MAX_LENGTH,
  STORE_DOMAIN_MAX_LENGTH,
  STORE_SLUG_MAX_LENGTH,
  STORE_SLUG_REGEX
} from '~/domain/dashboard/store/constants/storeBasicInfoLimits'

class AppStore extends BaseCRUDStore<App> {
  protected gateway = CPAppGateway.instance()

  protected findErrors (model: App): BaseError[] {
    const defaultErrors = (meta: Meta): BaseError => ({ code: ErrorCodes.DataValidation, meta })
    const errors: BaseError[] = []

    if (model.kind !== AppKind.Store && (model.name?.trim().length ?? 0) <= 0) {
      errors.push(defaultErrors({ name: MetaErrorCodes.Empty }))
    }

    if (model.kind === AppKind.Desktop) {
      if (
        model.settings.modules?.some((m) => {
          if (m.kind === DesktopModuleKind.KDS) {
            const kdsModule = m as DesktopModuleKDS
            return kdsModule.settings?.kds == null
          }
          return false
        })
      ) {
        errors.push(defaultErrors({ kds: MetaErrorCodes.Empty }))
      }
    }

    if (model.kind === AppKind.Store) {
      const storeNameLength = model.settings.basicInfo.name?.length ?? 0

      if (storeNameLength <= 0 || model.settings.basicInfo.name?.trim().length === 0) {
        errors.push(defaultErrors({
          'settings.basicInfo.name': MetaErrorCodes.Empty
        }))
      } else if (storeNameLength > STORE_DISPLAY_NAME_MAX_LENGTH) {
        errors.push(defaultErrors({
          'settings.basicInfo.name': MetaErrorCodes.LimitExceeded
        }))
      }

      const descriptionLength = model.settings.basicInfo.description?.length ?? 0
      if (descriptionLength > STORE_DESCRIPTION_MAX_LENGTH) {
        errors.push(defaultErrors({
          'settings.basicInfo.description': MetaErrorCodes.LimitExceeded
        }))
      }

      if ((model.domain?.length ?? 0) > STORE_DOMAIN_MAX_LENGTH) {
        errors.push(defaultErrors({
          domain: MetaErrorCodes.LimitExceeded
        }))
      }

      const slug = (model.settings.basicInfo.slug ?? '').trim()
      if (slug.length === 0) {
        errors.push(defaultErrors({
          'settings.basicInfo.slug': MetaErrorCodes.Empty
        }))
      } else if (slug.length > STORE_SLUG_MAX_LENGTH) {
        errors.push(defaultErrors({
          'settings.basicInfo.slug': MetaErrorCodes.LimitExceeded
        }))
      } else if (!STORE_SLUG_REGEX.test(slug)) {
        errors.push(defaultErrors({
          'settings.basicInfo.slug': MetaErrorCodes.WrongFormat
        }))
      }

      model.settings.contact.channels.forEach((channel, index) => {
        if (!channel.value || channel.value.trim().length === 0) {
          errors.push(defaultErrors({
            [`settings.contact.channels[${index}].value`]: MetaErrorCodes.Empty
          }))
        }
      })

      if ((model.settings.fulfillment?.fulfillmentsAvailable?.length ?? 0) === 0) {
        errors.push(defaultErrors({
          'settings.fulfillment.fulfillments_available': MetaErrorCodes.Empty
        }))
      }
    }

    if (model.kind === AppKind.SmartMenu) {
      if (model.settings.functionalities?.registration?.active && (model.settings.functionalities?.registration?.fields?.length ?? 0) === 0) {
        errors.push(defaultErrors({
          'settings.functionalities.registration.fields': MetaErrorCodes.Empty
        }))
      }
    }

    if (model.kind === AppKind.Totem) {
      const totem = model as TotemApp
      if (totem.settings.subtype === TotemSubtype.Scale) {
        if (totem.settings.baseProduct?.id == null) {
          errors.push(defaultErrors({ 'settings.baseProduct': MetaErrorCodes.Empty }))
        }
        if (totem.settings.peripherals?.scale?.id == null) {
          errors.push(defaultErrors({ 'settings.peripherals.scale': MetaErrorCodes.Empty }))
        }
      }
    }

    return errors
  }
}

const VALID_PUSH_STATUS_VALUES = new Set<number>(
  Object.values(PushNotificationStatus).filter(v => typeof v === 'number')
)

function toPushNotificationStatus (value: number): PushNotificationStatus | undefined {
  return VALID_PUSH_STATUS_VALUES.has(value) ? (value as PushNotificationStatus) : undefined
}

function mergeAppsWithPushStatus (apps: App[], statusMap: Record<string, number>): App[] {
  if (Object.keys(statusMap).length === 0) {
    return apps
  }
  return apps.map((app) => {
    if (!isAppSyncable(app)) {
      return app
    }
    const id = app.id != null ? String(app.id) : undefined
    const rawStatus = id != null ? statusMap[id] : undefined
    const status = rawStatus !== undefined ? toPushNotificationStatus(rawStatus) : undefined
    return status !== undefined ? { ...app, pushNotificationStatus: status } : app
  })
}

const useAppStore = defineStore('apps/app', () => {
  const appStore = new AppStore()
  const crudStore = appStore.defaultStore()
  const lastSavedApp = ref<App | undefined>()
  const baseSave = crudStore.save.bind(crudStore)

  const save = (
    model: App,
    afterSave: (savedModel: App) => Promise<void> = () => Promise.resolve(),
    key: keyof App | 'id' = 'id'
  ): Promise<boolean> => {
    lastSavedApp.value = undefined

    return baseSave(model, async (savedModel) => {
      lastSavedApp.value = savedModel
      await afterSave(savedModel)
    }, key)
  }

  function takeLastSavedApp (): App | undefined {
    const savedApp = lastSavedApp.value
    lastSavedApp.value = undefined
    return savedApp
  }

  const appGroups = ref<AppGroup[]>([])
  const appsCredentials = ref<Map<number, AppCredentials>>(new Map())
  const firebaseStatusPath = ref<string | null>(null)
  const { statusMap: pushNotificationStatusMap, isConnected: isPushStatusConnected, disconnect: disconnectPushStatus } =
    useAppPushNotificationStatus(firebaseStatusPath)

  const pageList = computed<App[]>(() =>
    mergeAppsWithPushStatus(crudStore.pageList.value ?? [], pushNotificationStatusMap.value)
  )

  async function connectPushNotificationStatus (): Promise<void> {
    const result = await CPAppGateway.instance().getPushNotificationConfig()
    result.ifSuccess((config) => {
      if (config.firebaseStatusPath) {
        firebaseStatusPath.value = config.firebaseStatusPath
      }
    })
  }

  function disconnectPushNotificationStatus (): void {
    firebaseStatusPath.value = null
    disconnectPushStatus()
  }

  async function getAppGroups (): Promise<void> {
    const result = await CPAppGroupGateway.instance().listAll()

    result.ifSuccess((pageList) => {
      appGroups.value = pageList.contentList
    })
  }

  async function restore (id: number): Promise<boolean> {
    const result = await CPAppGateway.instance().restore(id)

    result.ifError((error) => {
      crudStore.errors.value.push(error)
    })

    return result.isOk()
  }

  async function duplicate (app: App, options: AppDuplicatePayload): Promise<boolean> {
    const result = await CPAppGateway.instance().duplicate(app, options)

    return result.isOk()
  }

  async function getCredentials (appId: number): Promise<void> {
    const result = await CPAppGateway.instance().getCredentials(appId)

    if (result.isOk()) {
      appsCredentials.value?.set(appId, result.getValue())

      return
    }

    crudStore.errors.value.push(result.getError())
  }

  async function generateLinkQrToken (appId: number): Promise<AppLinkQrToken | null> {
    const result = await CPAppGateway.instance().generateLinkQrToken(appId)

    if (result.isOk()) {
      return result.getValue()
    }

    crudStore.errors.value.push(result.getError())

    return null
  }

  async function generateQuickLinkQrToken (): Promise<AppLinkQrToken | null> {
    const result = await CPAppGateway.instance().generateQuickLinkQrToken()

    if (result.isOk()) {
      return result.getValue()
    }

    crudStore.errors.value.push(result.getError())

    return null
  }

  async function revokeLicense (appId: number, payload: AppRevokeLicensePayload): Promise<boolean> {
    const result = await CPAppGateway.instance().revokeLicense(appId, payload)

    result.ifError((error) => {
      const meta = error.meta ? Object.assign({}, error.meta) : {}
      Object.entries(meta).forEach(([key, value]) => {
        crudStore.errors.value.push({ ...error, message: '', meta: { [key]: value } })
      })
    })

    return result.isOk()
  }

  async function updateVpnSettings (appId: number, payload: VpnSetting): Promise<boolean> {
    const result = await CPAppGateway.instance().updateVpnSettings(appId, payload)

    result.ifError((error) => {
      crudStore.errors.value.push(error)
    })

    return result.isOk()
  }

  async function downloadTableQrCode (appId: number, tableId?: number | null): Promise<Blob | false> {
    const result = await CPAppGateway.instance().downloadTableQrCode(appId, tableId)

    if (result.isError()) {
      crudStore.errors.value.push(result.getError())
      return false
    }

    return result.getValue()
  }

  async function downloadQrCode (appId: number, functionality: SmartMenuFunctionality): Promise<Blob | false> {
    const result = await CPAppGateway.instance().downloadQrCode(appId, functionality)

    if (result.isError()) {
      crudStore.errors.value.push(result.getError())
      return false
    }

    return result.getValue()
  }

  async function sync (apps: App[]): Promise<boolean> {
    const appIds = apps.map(app => app.id).filter((id): id is number => id != null)

    if (appIds.length === 0) {
      return false
    }

    const result = await CPAppGateway.instance().sync(appIds)

    result.ifError((error) => {
      crudStore.errors.value.push(error)
    })

    return result.isOk()
  }

  return {
    ...crudStore,
    save,
    takeLastSavedApp,
    pageList,
    appGroups,
    getAppGroups,
    restore,
    duplicate,
    getCredentials,
    appsCredentials,
    generateLinkQrToken,
    generateQuickLinkQrToken,
    revokeLicense,
    updateVpnSettings,
    sync,
    downloadTableQrCode,
    pushNotificationStatusMap,
    isPushStatusConnected,
    connectPushNotificationStatus,
    disconnectPushNotificationStatus,
    downloadQrCode
  }
})

export default useAppStore

/* c8 ignore next 3 */
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useAppStore, (import.meta as any).hot))
}
